import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGoogleMap } from '@react-google-maps/api';

// Subclass google.maps.OverlayView and update DOM position in draw().
// @react-google-maps/api's <OverlayView> wraps projection updates in React
// render cycles, which lag the tile transform by ~1 frame on fast drags.
// This causes flashing of items rendered in the OverlayView.
// Doing this imperatively keeps the overlay synced with the tile pass.
//
// Defined lazily because google.maps.OverlayView doesn't exist until the
// Maps script is loaded.
type ImperativeOverlay = google.maps.OverlayView & {
  getContainer: () => HTMLDivElement;
  setPosition: (position: google.maps.LatLngLiteral) => void;
};
let OverlayClass:
  | (new (
      map: google.maps.Map,
      position: google.maps.LatLngLiteral,
      pane: keyof google.maps.MapPanes,
    ) => ImperativeOverlay)
  | null = null;

const getOverlayClass = () => {
  if (OverlayClass) {
    return OverlayClass;
  }
  class Overlay extends google.maps.OverlayView {
    private container = document.createElement('div');
    private position: google.maps.LatLngLiteral;
    private pane: keyof google.maps.MapPanes;

    constructor(
      map: google.maps.Map,
      position: google.maps.LatLngLiteral,
      pane: keyof google.maps.MapPanes,
    ) {
      super();
      this.position = position;
      this.pane = pane;
      this.container.style.position = 'absolute';
      this.setMap(map);
    }

    onAdd() {
      this.getPanes()?.[this.pane].appendChild(this.container);
    }

    draw() {
      const projection = this.getProjection();
      if (!projection) {
        return;
      }
      const pixel = projection.fromLatLngToDivPixel(
        new google.maps.LatLng(this.position),
      );
      if (!pixel) {
        return;
      }
      this.container.style.left = `${pixel.x}px`;
      this.container.style.top = `${pixel.y}px`;
    }

    onRemove() {
      this.container.remove();
    }

    setPosition(next: google.maps.LatLngLiteral) {
      this.position = next;
      this.draw();
    }

    getContainer() {
      return this.container;
    }
  }
  OverlayClass = Overlay;
  return OverlayClass;
};

interface Props {
  children: React.ReactNode;
  /**
   * Which map pane to render into. Mouse-target receives DOM events;
   * marker layer is below the mouse-target pane.
   */
  pane?: keyof google.maps.MapPanes;
  position: google.maps.LatLngLiteral;
}

export const ImperativeOverlayView: React.FC<Props> = ({
  children,
  pane = 'overlayMouseTarget',
  position,
}) => {
  const map = useGoogleMap();
  const overlayRef = useRef<ImperativeOverlay | null>(null);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!map) {
      return;
    }
    const OverlayCtor = getOverlayClass();
    const overlay = new OverlayCtor(map, position, pane);
    overlayRef.current = overlay;
    setContainer(overlay.getContainer());
    return () => {
      overlay.setMap(null);
      overlayRef.current = null;
      setContainer(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, pane]);

  useEffect(() => {
    overlayRef.current?.setPosition(position);
    // Deps are the primitives so a fresh `position` object with the same
    // lat/lng doesn't re-fire and trigger an extra DOM mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position.lat, position.lng]);

  if (!container) {
    return null;
  }
  return createPortal(children, container);
};
